/**
 * List filters for `OperationsList`, declared once for BOTH read paths.
 *
 * ## Why this module exists (ATL-986)
 *
 * `OperationsList` has two read paths: the legacy Firestore query builder and the
 * Supabase-backed REST API. A screen-level constraint ("only expenses", "only this
 * supplier") therefore has two representations, and they cannot be derived from one
 * another: the Firestore half is an opaque closure `(query) => query.where(...)` that
 * no amount of introspection can turn into a query string.
 *
 * Before ATL-986 the two halves were independent optional props (`additionalFilters`
 * and `additionalApiParams`). A caller that supplied only the Firestore closure got a
 * correct list on Firestore and an UNFILTERED list on Supabase — silently, with no
 * error and no parity signal, because every row involved is present and correct in
 * both stores. `/compare`, the id-level set-diff and the completeness census all read
 * clean while a merchant looked at the wrong money.
 *
 * The fix is structural: one prop carrying both halves, both of them required. A
 * caller can no longer express "filter here but not there" by omission — only
 * deliberately, by writing `api: null`, which makes the Supabase read path REFUSE to
 * render rather than render unfiltered rows.
 *
 * Note that `apps/atlas` compiles with `checkJs: false` and lints only `.ts`/`.tsx`,
 * and every screen that renders `OperationsList` is a `.js` file. The type below
 * therefore documents the contract but does not enforce it at the call sites, which
 * is exactly how the original defect shipped. `resolveOperationsFilter` is the
 * runtime enforcement, and it is what `OperationsList` actually relies on.
 */

/**
 * A Firestore query/collection reference as consumed by the legacy read path.
 *
 * Deliberately `unknown`: `OperationsList` never inspects it, it only threads it
 * through the caller's closure and back into the Firestore SDK.
 */
export type FirestoreQuery = unknown

/** Query-string parameters understood by the backend list endpoints. */
export type OperationsApiParams = Record<string, string | number | boolean>

/**
 * A list filter expressed for both read paths.
 *
 * @property firestore - Applied by the Firestore read path.
 * @property api - The EQUIVALENT constraint as backend query params, applied by the
 *   Supabase read path. Use `{}` for "this filter is a no-op right now" (e.g. an
 *   optional id prop that is absent). Use `null` ONLY when the backend genuinely
 *   cannot express the constraint — the Supabase read path will then refuse to load
 *   the list instead of showing unfiltered data.
 */
export interface OperationsFilter {
  firestore: (query: FirestoreQuery) => FirestoreQuery
  api: OperationsApiParams | null
}

/** The outcome of validating whatever a caller passed as a filter. */
export type OperationsFilterResolution =
  | {
      ok: true
      /** `null` when there is no Firestore-side constraint to apply. */
      firestore: ((query: FirestoreQuery) => FirestoreQuery) | null
      /** Params to merge into the API request. Always an object when `ok`. */
      api: OperationsApiParams
    }
  | {
      ok: false
      /**
       * `'firestore-only'` — the caller DELIBERATELY declared `api: null`. The
       * Firestore read path is the intended one and applies the filter normally; only
       * the Supabase read path must refuse. This is not a defect and is not reported.
       *
       * `'invalid'` — the declaration is malformed or is the pre-ATL-986 bare closure.
       * Worth reporting wherever it is seen.
       */
      kind: 'firestore-only' | 'invalid'
      /** Human-readable reason, surfaced in the UI and (for `'invalid'`) the console. */
      reason: string
      /**
       * `true` when the value is a usable Firestore closure that merely lacks an API
       * equivalent. The Firestore read path can still honour it; the Supabase read
       * path cannot and must refuse.
       */
      firestoreUsable: boolean
      firestore: ((query: FirestoreQuery) => FirestoreQuery) | null
    }

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Every API param value must survive `?a=b` serialisation unambiguously. Objects,
 * arrays, `undefined` and `null` do not, and silently serialising them is how a
 * filter turns into `[object Object]` and matches nothing.
 */
const isSerializableParamValue = (value: unknown): value is string | number | boolean =>
  (typeof value === 'string' && value.length > 0) ||
  (typeof value === 'number' && Number.isFinite(value)) ||
  typeof value === 'boolean'

/**
 * Validate a caller-supplied filter and split it into the two read paths.
 *
 * Accepts `null`/`undefined` (no filter). Rejects the pre-ATL-986 shape (a bare
 * function) and any paired filter whose `api` half is missing or unserialisable.
 */
export const resolveOperationsFilter = (value: unknown): OperationsFilterResolution => {
  if (value === null || value === undefined) {
    return { ok: true, firestore: null, api: {} }
  }

  if (typeof value === 'function') {
    // The pre-ATL-986 shape. Reachable from unchecked `.js` screens.
    return {
      ok: false,
      kind: 'invalid',
      reason:
        'OperationsList filter is a bare Firestore closure with no API equivalent. ' +
        'Pass { firestore, api } instead (ATL-986).',
      firestoreUsable: true,
      firestore: value as (query: FirestoreQuery) => FirestoreQuery,
    }
  }

  if (!isPlainRecord(value)) {
    return {
      ok: false,
      kind: 'invalid',
      reason: `OperationsList filter must be a { firestore, api } object, received ${typeof value}.`,
      firestoreUsable: false,
      firestore: null,
    }
  }

  const firestore = typeof value.firestore === 'function'
    ? (value.firestore as (query: FirestoreQuery) => FirestoreQuery)
    : null

  if (firestore === null) {
    return {
      ok: false,
      kind: 'invalid',
      reason: 'OperationsList filter is missing its `firestore` function.',
      firestoreUsable: false,
      firestore: null,
    }
  }

  if (!('api' in value)) {
    return {
      ok: false,
      kind: 'invalid',
      reason:
        'OperationsList filter declares `firestore` but not `api`, so the Supabase ' +
        'read path would silently drop it (ATL-986).',
      firestoreUsable: true,
      firestore,
    }
  }

  if (value.api === null) {
    return {
      ok: false,
      kind: 'firestore-only',
      reason:
        'OperationsList filter declares `api: null` — the backend cannot express this ' +
        'constraint, so the Supabase read path must not render this list.',
      firestoreUsable: true,
      firestore,
    }
  }

  if (!isPlainRecord(value.api)) {
    return {
      ok: false,
      kind: 'invalid',
      reason: `OperationsList filter \`api\` must be an object or null, received ${typeof value.api}.`,
      firestoreUsable: true,
      firestore,
    }
  }

  const api: OperationsApiParams = {}
  for (const [key, paramValue] of Object.entries(value.api)) {
    if (paramValue === undefined) continue // an absent optional constraint
    if (!isSerializableParamValue(paramValue)) {
      return {
        ok: false,
        kind: 'invalid',
        reason: `OperationsList filter param \`${key}\` is not serialisable as a query param.`,
        firestoreUsable: true,
        firestore,
      }
    }
    api[key] = paramValue
  }

  return { ok: true, firestore, api }
}

/** Convenience wrapper used by tests and by callers that only need the API half. */
export const operationsFilterApiParams = (value: unknown): OperationsApiParams | null => {
  const resolved = resolveOperationsFilter(value)
  return resolved.ok ? resolved.api : null
}

/** Inputs to a Supabase-path list request, before the caller's filter is applied. */
export interface SupabaseListParamsInput {
  limit: number
  offset: number
  /** Resolved store id, or `'all'` / `null` for "every store". */
  storeId?: string | null
  /** ISO strings; both must be present for the range to be applied. */
  startDate?: string | null
  endDate?: string | null
  /** The filter declared by the screen, exactly as it was passed in. */
  filter?: unknown
}

/**
 * Build the query params for a Supabase-path list request.
 *
 * Returns `null` when the caller's filter cannot be applied on this read path — the
 * caller MUST NOT fall back to an unfiltered request in that case. Returning params
 * that quietly omit a declared constraint is the ATL-986 defect.
 */
export const buildSupabaseListParams = (
  input: SupabaseListParamsInput
): OperationsApiParams | null => {
  const resolved = resolveOperationsFilter(input.filter)
  if (!resolved.ok) return null

  const params: OperationsApiParams = {
    limit: input.limit,
    offset: input.offset,
    ...resolved.api,
  }

  if (input.storeId && input.storeId !== 'all') {
    params.storeId = input.storeId
  }

  if (input.startDate && input.endDate) {
    params.startDate = input.startDate
    params.endDate = input.endDate
  }

  return params
}
